import React, { useEffect, useRef, useState } from 'react';
import { useExpandAnimation, useAnimatedPresence } from '../hooks/useExpandAnimation';
import PaperclipIcon from './ui/PaperclipIcon';
import { DESKTOP_SECTION_CHEVRON, DESKTOP_SECTION_TITLE, DESKTOP_SECTION_HEAD_BLEED } from './OrderSectionHeader';
import UploadingFileRow from './UploadingFileRow';
import PendingUploadRow from './PendingUploadRow';
import { formatFileSize } from '../utils/formatFileSize';

/**
 * THE order file-attachment section. One definition, every surface.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A COMPONENT AND NOT A BLOCK INSIDE `OrderForm`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Extracted from `components/OrderForm.js` on 2026-08-10, VERBATIM — same
 * markup, same classes, same `data-testid`s, same collapsed-by-default
 * disclosure — when the expanded order row on Department Orders / To Approve
 * needed attachments too.
 *
 * 🔑 THE EXTRACTION IS THE POINT, NOT A TIDY-UP. The expanded panel has TWO
 * branches (`OrderForm` when the server says `can_edit`, a read-only panel
 * otherwise) and the READ-ONLY branch has no `OrderForm` at all — so wiring
 * `OrderForm`'s `showOrderFileUpload` would have given attachments to editors
 * only, and a second hand-rolled list to everyone else. That is the Code
 * Unification Principle's exact failure case: the same thing drawn twice,
 * diverging the first time a threshold or a label is retuned.
 *
 * `OrderForm` now renders this, so `pages/NewOrder.js` and `pages/OrderPage.js`
 * are unaffected — the default `testIdPrefix` of `''` reproduces their existing
 * testids character for character.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE UPLOAD POLICY — DO NOT "HARDEN" ANY OF IT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🚨 NO `accept=` FILTER ON THE INPUT, DELIBERATELY. An order attachment is a
 * GENERAL-PURPOSE surface, filtered server-side by the shared blocklist
 * `BLOCKED_UPLOAD_EXT` (`server/helpers/uploads.js`) with magic-byte
 * verification and UTF-8 filename handling. CLAUDE.md forbids this by name:
 * *"Don't put a fixed `accept=` list on a general-purpose file input — that
 * silently re-imposes an allowlist."* See
 * `docs/superpowers/specs/2026-07-30-upload-file-type-policy-design.md`.
 *
 * 🔒 DOWNLOADS GO THROUGH `file.file_path`, i.e. `/uploads/order-<id>/<name>`,
 * which is the AUTHENTICATED router `server/routes/uploads.js` — it re-runs the
 * same `canViewOrder` gate as the listing endpoint and 404s misses and denials
 * identically so it cannot be used to enumerate files. It is NOT
 * `express.static`. Never rebuild these links against a raw path.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * PERMISSIONS — THIS COMPONENT DECIDES NOTHING
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * It renders what its props allow and asks no authority question of its own:
 *   - the drop zone appears iff `onFilesChange` is a function;
 *   - a per-file Remove appears iff `onExistingFileToggleRemove` is a function
 *     AND `canRemoveFile(file)` returns true.
 *
 * 🔑 `canRemoveFile` IS PER FILE BECAUSE THE SERVER GATE IS PER FILE.
 * `DELETE /api/orders/:id/files/:fileId` (`server/routes/orders.js:3414-3432`)
 * admits: a global admin or an in-department buyer (moderators, any file); the
 * file's own UPLOADER (`uploaded_by_user_id === caller`); or, for legacy rows
 * predating uploader tracking (`uploaded_by_user_id IS NULL`), anyone passing
 * `canEditOrder`. A single boolean for the whole list cannot express that, and
 * the two existing surfaces each approximate it differently — `OrderPage`'s
 * order attachments use `order.can_edit` for every file while its delivery
 * paperwork uses `isAdmin || file.uploaded_by_user_id === user.id`. Callers
 * that want the old all-or-nothing behaviour pass the default.
 */

// The chrome of an uploading / failed / pending row — the listed row's box, so a
// row keeps its place and size as it turns from uploading → listed.
const UPLOADING_ROW_CHROME = 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-xs';

// Re-exported: `components/OrderForm.js` imports it from here (2026-09-15, moved to utils).
export { formatFileSize };

function OrderAttachments({
  title = 'Attachments',
  // 📱 Optional phone label (owner 2026-10-03, New Order: '+ Order attachments'):
  // below md the chevron hides and the label reads '+ <phoneTitle>' (− while open).
  phoneTitle = null,
  // Already-saved rows from `order_files` (each: id, file_name, file_path,
  // file_size, uploaded_by_user_id, category).
  existingFiles = [],
  // 🔻 `stagedFiles` / `stagedProgressFor` and the "Staged" row were DELETED
  // 2026-09-15 (R13, owner: *"What does staged mean? If this means uploaded please
  // chnge it to uploaded"*): a file is uploaded at pick now — immediately, or to
  // the pending store (`pendingEntries` below).
  // Called with the files just picked or dropped. Null → no drop zone at all.
  onFilesChange = null,
  // Ids marked for deletion on save (deferred-delete surfaces only). A file in
  // this set renders struck through with an Undo instead of a Remove.
  pendingDeleteFileIds = null,
  onExistingFileToggleRemove = null,
  // Per-file predicate; see the docblock. Default preserves the caller
  // behaviour this was extracted from.
  canRemoveFile = () => true,
  // Optional per-file attribution chip, e.g. `Item 2 (SF-25)`. Return a falsy
  // value for a file that needs none.
  //
  // 🔑 A LABEL ON ONE LIST, NOT A SECOND LIST. Added 2026-08-11 so the expanded
  // order row could show LINE-ITEM attachments — which were invisible there —
  // without splitting the Attachments section into per-item groups. See
  // `components/OrderExpandedDetails.js` for why grouping was rejected; the
  // short version is that its EDIT branch cannot render per-item attachments at
  // all without giving `OrderInlineEditor` a file flush it deliberately lacks.
  //
  // ⚠️ Defaults to a function returning null, so `pages/OrderPage.js`,
  // `pages/NewOrder.js` and the delivery-paperwork render are byte-identical to
  // what they produced before this prop existed — no chip, no extra element, no
  // new testid.
  fileLabel = () => null,
  // 🔴 COLLAPSED BY DEFAULT on every surface, and that is inherited from
  // `OrderForm`, not invented here — the disclosure already opened closed.
  defaultOpen = false,
  // '' reproduces `OrderForm`'s original testids exactly. Any surface that
  // renders TWO of these on one page (the expanded panel renders order
  // attachments AND delivery paperwork) MUST pass distinct prefixes:
  // CLAUDE.md — "TWO COMPONENTS MUST NEVER STAMP THE SAME `data-testid` ON ONE
  // PAGE", because `container.querySelector` returns the first match and
  // reports no ambiguity, so the test silently stops being about what it says.
  testIdPrefix = '',
  // ── UPLOAD PROGRESS (2026-09-14) — defaults to nothing, so every caller that
  // passes none renders byte-identically. `uploadingEntries` are
  // `hooks/useUploadProgress` entries `{ key, file, fraction, error }` for files
  // on their way up (or refused). See `UploadingFileRow`.
  uploadingEntries = [],
  onDismissUpload = null,
  // ── PENDING UPLOADS (2026-09-15, R13) — entries from
  // `hooks/usePendingOrderUploads.js` for files uploaded on pick and attached by
  // the save; each renders as `PendingUploadRow` ("Uploaded", bar, or refusal).
  // Default empty → byte-identical for every caller that passes none.
  pendingEntries = [],
  onPendingRemove = null,
  onPendingRetry = null,
  // Shown when there is nothing to list and nothing can be attached.
  emptyText = null,
  className = 'border-t border-gray-200 dark:border-gray-700 pt-2',
  // 🖥️ true only where this section is its OWN card (the opened order's lower
  // sections): its header then bleeds to the card edges and hovers as one bar
  // (`DESKTOP_SECTION_HEAD_BLEED`, OrderSectionHeader.js). Inside another card
  // (OrderForm's Notes card on New Order) it must not bleed.
  desktopSectionHead = false,
}) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  // The body eases open AND closed (owner 2026-10-09) — `hooks/useExpandAnimation.js`;
  // `bodyShown` keeps it mounted for the closing ease. Not on the FIRST render:
  // a section that starts open inside a freshly opened order must not animate
  // on top of the order's own opening.
  const bodyRef = useRef(null);
  const hasRendered = useRef(false);
  const bodyShown = useAnimatedPresence(open, bodyRef);
  useExpandAnimation(bodyRef, bodyShown, { animateOnMount: hasRendered.current });
  useEffect(() => { hasRendered.current = true; }, []);

  const canAttach = typeof onFilesChange === 'function';
  const canToggleRemove = typeof onExistingFileToggleRemove === 'function';
  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix);

  if (existingFiles.length === 0 && uploadingEntries.length === 0 && pendingEntries.length === 0 && !canAttach && !emptyText) {
    return null;
  }

  const visibleCount =
    existingFiles.filter(f => !(pendingDeleteFileIds && pendingDeleteFileIds.has(f.id))).length
    + pendingEntries.length;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && onFilesChange) onFilesChange(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onFilesChange) onFilesChange(files);
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(prev => !prev); }}
        className={`flex items-center gap-2 w-full text-left${desktopSectionHead ? ` ${DESKTOP_SECTION_HEAD_BLEED}` : ''}`}
        aria-expanded={open}
        data-testid={tid('attachments-toggle')}
        data-phone-section-head=""
      >
        <svg data-chevron xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${DESKTOP_SECTION_CHEVRON} text-gray-400 transition-transform ${open ? '' : '-rotate-90'}${phoneTitle ? ' max-md:hidden' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
        <span className={`text-sm font-medium text-gray-600 dark:text-gray-400 ${DESKTOP_SECTION_TITLE}${phoneTitle ? ' max-md:hidden' : ''}`}>{title}</span>
        {/* 📱 Phone (owner 2026-10-03): a paperclip and the title. Tapping the
            words only opens / closes the section like the rest of the header —
            files are viewed and attached INSIDE the opened section (owner,
            same day: not by tapping the collapsed header). */}
        {phoneTitle && (
          <span className="md:hidden inline-flex items-center gap-1.5 min-w-0 text-sm font-medium text-gray-600 dark:text-gray-400">
            <PaperclipIcon className="h-4 w-4 shrink-0 text-gray-400" />
            <span className="truncate" data-testid={tid('phone-attach-label')}>{phoneTitle}</span>
          </span>
        )}
        {visibleCount > 0 && (
          <span className="text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">{visibleCount} file(s)</span>
        )}
      </button>
      {bodyShown && (
        <div ref={bodyRef} className="mt-2 space-y-2">
          {canAttach && (
            <div
              className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'}`}
              onClick={(e) => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid={tid('file-upload-area')}
              role="button"
              aria-label={`Click or drag files to upload ${title.toLowerCase()}`}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); } }}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">{dragOver ? 'Drop files here' : 'Click or drag files to attach'}</p>
              <p className="text-xs text-gray-400 mt-0.5">Any working file (max 20MB each)</p>
              {/*
                No `accept` filter, deliberately — see this file's docblock. The
                server filters with the BLOCKED_UPLOAD_EXT blocklist; a fixed
                picker list here would silently re-impose the allowlist the
                2026-07-30 policy removed.
              */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                aria-label={`Upload ${title.toLowerCase()}`}
                data-testid={tid('file-input')}
              />
            </div>
          )}
          {(existingFiles.length > 0 || uploadingEntries.length > 0 || pendingEntries.length > 0) ? (
            <div className="space-y-1.5" data-testid={tid('file-list')}>
              {existingFiles.map(file => {
                const isPending = pendingDeleteFileIds && pendingDeleteFileIds.has(file.id);
                const label = fileLabel(file);
                return (
                  <div key={`existing-${file.id}`} className={`flex items-center justify-between bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-xs ${isPending ? 'opacity-60' : ''}`} data-testid={tid(`order-existing-file-${file.id}`)}>
                    <div className={`flex items-center gap-1.5 min-w-0 ${isPending ? 'line-through' : ''}`}>
                      {/* The paperclip + filename ARE the download control — same
                          `href`/`download` as the "Download" button below, so the
                          request goes through the AUTHENTICATED `/uploads/*`
                          router (see this file's docblock), never a raw path. A
                          real `<a>` is keyboard-focusable and carries an explicit
                          "Download <name>" label. A file already marked for
                          removal shows no link, exactly as the Download button is
                          hidden while `isPending`. */}
                      {isPending ? (
                        <>
                          <PaperclipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 truncate">{file.file_name}</span>
                        </>
                      ) : (
                        <a
                          href={file.file_path}
                          download={file.file_name}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 min-w-0 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                          aria-label={`Download ${file.file_name}`}
                          data-testid={tid(`download-name-order-existing-${file.id}`)}
                        >
                          <PaperclipIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:underline truncate">{file.file_name}</span>
                        </a>
                      )}
                      {file.file_size != null && (
                        <span className="text-gray-400 flex-shrink-0">({formatFileSize(file.file_size)})</span>
                      )}
                      {/* The line this file belongs to, when the caller can say.
                          🔑 AFTER the size and `flex-shrink-0`: the FILENAME is
                          what the user is looking for, so it owns the truncation
                          and the attribution keeps its full width. */}
                      {label && (
                        <span
                          className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 rounded px-1.5 py-0.5 flex-shrink-0 max-w-[45%] truncate"
                          data-testid={tid(`file-label-${file.id}`)}
                          title={label}
                        >
                          {label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {!isPending && (
                        <a
                          href={file.file_path}
                          download={file.file_name}
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                          data-testid={tid(`download-order-existing-${file.id}`)}
                        >
                          Download
                        </a>
                      )}
                      {canToggleRemove && canRemoveFile(file) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onExistingFileToggleRemove(file.id); }}
                          className={`${isPending ? 'text-blue-600 hover:text-blue-800' : 'text-red-500 hover:text-red-700'} font-medium`}
                          aria-label={isPending ? `Undo remove ${file.file_name}` : `Remove ${file.file_name}`}
                          data-testid={tid(`toggle-remove-order-existing-${file.id}`)}
                        >
                          {isPending ? 'Undo' : 'Remove'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {pendingEntries.map((entry) => (
                <PendingUploadRow
                  key={entry.key}
                  entry={entry}
                  onRemove={(key) => onPendingRemove && onPendingRemove(key)}
                  onRetry={onPendingRetry}
                  className={UPLOADING_ROW_CHROME}
                  testId={tid(`pending-${entry.key}`)}
                />
              ))}
              {uploadingEntries.map((entry) => (
                <UploadingFileRow
                  key={entry.key}
                  file={entry.file}
                  fraction={entry.fraction}
                  error={entry.error}
                  onDismiss={onDismissUpload ? () => onDismissUpload(entry.key) : null}
                  className={UPLOADING_ROW_CHROME}
                  testId={tid(`uploading-${entry.key}`)}
                />
              ))}
            </div>
          ) : emptyText ? (
            <p className="text-xs text-gray-400 italic" data-testid={tid('no-files')}>{emptyText}</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default OrderAttachments;
